import {
  ForwardRefRenderFunction,
  MouseEvent,
  MutableRefObject,
  RefObject,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { PointCloudOctree, Potree } from '@datalabs-jp/three-loader'
import { focusCamera, getCenterAndRadius } from '@editor/services/Editor'
import { withPromiseContained } from '@editor/services/Util'
import { ArcballControls } from '@editor/services/threejs'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { Matrix4, PerspectiveCamera, Points, Scene, Vector3 } from 'three'
import { useDebouncedCallback, useThrottledCallback } from 'use-debounce'

import usePCDLoader from '../../hooks/usePCDLoader'
import { AlignedArea, SceneEventHandlers, Tools } from '../definitions'
import { usePointSelection } from '../hooks/usePointSelection'
import { setCurrentHoveringArea, setIsLoading, setLinkedAreas, setRightProgress } from '../store'
import { collectConnectedAreas, loadAllPCDs } from '../utils'

export interface MainSceneProps {
  sceneRef: RefObject<Scene>
  controlsRef: MutableRefObject<ArcballControls | null>
  camera: PerspectiveCamera
  potree: Potree
}

const MainSceneFn: ForwardRefRenderFunction<SceneEventHandlers, MainSceneProps> = (
  { sceneRef, controlsRef, camera, potree },
  ref,
) => {
  // States
  const [pointClouds, setPointClouds] = useState<Record<string, Points | PointCloudOctree>>({})
  const [initCompleted, setInitCompleted] = useState(false)
  const [bboxCenter, setBboxCenter] = useState<Vector3 | null>(null)
  const [radius, setRadius] = useState(0)

  // Hooks
  const { loadPCD } = usePCDLoader(potree)
  const { projectPoint, getProjectedAreaId } = usePointSelection(Object.values(pointClouds), camera)

  // Store
  const dispatch = useAppDispatch()
  const inspectionAreas = useSelector((state: RootState) => state.page.inspectionAreas)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const currentTool = useSelector((state: RootState) => state.pcdAlignment.currentTool)

  const connectedAreas = useMemo(() => {
    if (!inspectionArea) return []

    return collectConnectedAreas(
      {
        current: {
          area: inspectionArea,
          anchors: [],
          center: new Vector3(),
        },
        matrix: new Matrix4().identity(),
        inverted: false,
      },
      inspectionArea,
      inspectionAreas,
    )
  }, [inspectionArea, inspectionAreas])

  /**
   * Save all connected area IDs to the store.
   */
  useEffect(() => {
    dispatch(setLinkedAreas(connectedAreas.map((area) => area.current.area.inspection_area_id)))
  }, [connectedAreas, dispatch])

  /**
   * Load point clouds for all connected areas.
   */
  const init = useDebouncedCallback(
    (connections: AlignedArea[] | null) => {
      if (!connections?.length) return

      void withPromiseContained(async () => {
        const allProgress = new Map<string, number>()
        let total = 0
        const pcds = (
          await Promise.all(
            loadAllPCDs(connections, null, loadPCD, (areaId, percent) => {
              allProgress.set(areaId, percent)
              total = Math.max(
                total,
                Array.from(allProgress.values()).reduce((acc, cur) => acc + cur, 0),
              )
              dispatch(setRightProgress(total / connections.length))
            }),
          )
        ).filter((p) => p?.pcd) as { areaId: string; pcd: Points | PointCloudOctree }[]

        if (!pcds[0]?.pcd) return

        // get bounding box center and radius
        const { radius: rds, center } = getCenterAndRadius(pcds.map((p) => p.pcd))

        setBboxCenter(center)
        setRadius(rds)

        const toStore = pcds.reduce((acc, p) => {
          if (!p?.pcd) return acc
          return { ...acc, [p.areaId]: p.pcd }
        }, {})
        setPointClouds(toStore)
      }).finally(() => {
        dispatch(setIsLoading(false))
        dispatch(setRightProgress(null))
        setInitCompleted(true)
      })
    },
    1000,
    { leading: true, trailing: true },
  )

  useEffect(() => {
    if (initCompleted || currentTool !== Tools.Overview) return

    init(connectedAreas)
  }, [init, connectedAreas, initCompleted, currentTool, loadPCD, dispatch])

  useEffect(() => {
    if (currentTool !== Tools.Overview) return

    setInitCompleted(false)
  }, [inspectionArea, currentTool])

  /**
   * Unload point clouds when the area changes.
   */
  useEffect(() => {
    if (currentTool !== Tools.Overview && Object.keys(pointClouds).length > 0) {
      setInitCompleted(false)
      setPointClouds({})
    }
  }, [currentTool, pointClouds])

  /**
   * Throttle the hover area event to avoid too many raycasting calls.
   * This is important for performance, especially when the user is moving the mouse quickly.
   */
  const setHoverArea = useThrottledCallback((ev: MouseEvent<HTMLDivElement>) => {
    const areaId = getProjectedAreaId(ev)
    dispatch(setCurrentHoveringArea(areaId))
  }, 100)

  /**
   * Handle user interaction event.
   */
  useImperativeHandle(ref, () => ({
    onFocus: (ev) => {
      if (!initCompleted) return
      const point = projectPoint(ev)
      if (!controlsRef.current || !point) return
      focusCamera(controlsRef.current, camera, point, Infinity)
    },
    onMove: (ev) => {
      if (!initCompleted) return
      setHoverArea(ev)
    },
  }))

  useEffect(() => {
    const canvasBox: HTMLCanvasElement | null = document.querySelector('#canvasBox canvas')
    if (!sceneRef.current || !canvasBox || !Object.keys(pointClouds).length || !bboxCenter || !inspectionArea)
      return () => null

    controlsRef.current = new ArcballControls(camera, canvasBox, sceneRef.current)
    controlsRef.current.enableFocus = false
    controlsRef.current.enableAnimations = false
    controlsRef.current.activateGizmos(false)
    controlsRef.current.setGizmosVisible(false)

    if (inspectionArea?.camera_profile) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      ;(controlsRef.current as any).setStateFromJSON(JSON.stringify(inspectionArea.camera_profile.state))
    } else {
      camera.position.copy(bboxCenter.clone().setZ(bboxCenter.z + radius))
      focusCamera(controlsRef.current, camera, bboxCenter, 1)
    }

    return () => {
      controlsRef.current?.dispose()
      controlsRef.current = null
    }
  }, [controlsRef, camera, sceneRef, inspectionArea, pointClouds, bboxCenter, radius])

  useEffect(() => {
    if (!controlsRef.current) return
    controlsRef.current.enabled = currentTool === Tools.Overview
  }, [currentTool, controlsRef])

  return (
    <scene ref={sceneRef}>
      {Object.keys(pointClouds).map((areaId) => (
        <primitive key={areaId} object={pointClouds[areaId]} />
      ))}

      <ambientLight intensity={Math.PI} />
    </scene>
  )
}

const MainScene = forwardRef(MainSceneFn)
export default MainScene
